import { useEffect, useRef, useState } from "react";
import { apiApont, type LancamentoProduto } from "../lib/api";
import { MsgModal } from "./MsgModal";
import { Confirma, Modal, enterTab, parseBR, soNum } from "./EstoqueUi";

// acompanhamento_pcf / lancamento_produtos "Cadastro de informações do lançamento de produto - Etapas da Produção" +
// local_estoque (local + senha de encarregado). Legado novo (C:\formsteel_d), 22/10/2026. Abre no Finalizar da Inspeção
// Final quando a PCF não tem lotes lançados; Confirmar devolve o lançamento para a finalização gravar tudo numa transação.
// As validações são as do legado (lp:747-820) e o servidor revalida.

interface Props {
  numpcf: number;
  etapa: string;
  posicao: number;
  onConfirmar: (l: LancamentoProduto) => void;
  onVoltar: () => void;
}

export function LancamentoProdutoModal({ numpcf, etapa, posicao, onConfirmar, onVoltar }: Props) {
  const [f, setF] = useState({ unidade: "", peso: "", compmin: "", compmax: "", qtdpec: "", metros: "", sucata: "", compminP: "", compmaxP: "", pontas: "" });
  const [local, setLocal] = useState<{ codloc: number; nomloc: string; senha: string } | null>(null);
  const [abrirLocal, setAbrirLocal] = useState(false);
  const [nompro, setNompro] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const raiz = useRef<HTMLDivElement>(null);
  const set = (k: keyof typeof f, casas: number) => (e: React.ChangeEvent<HTMLInputElement>) => setF({ ...f, [k]: soNum(e.target.value, casas) });

  useEffect(() => {
    apiApont.lancamentoDados(numpcf).then((d) => setNompro(d.nompro ?? "")).catch(() => setNompro(""));
  }, [numpcf]);

  // botao-confirmar (lp:740-783) + MOVER-CAMPOS (lp:786-820)
  function confirmar() {
    const v = (s: string) => parseBR(s);
    if (!v(f.compmin)) return setErro("Comprimento mínimo não informadas!");
    if (!v(f.compmax)) return setErro("Comprimento máximo não informado!");
    if (!v(f.peso) || !v(f.qtdpec)) return setErro("Quantidades Produzidas não informada!");
    if (!f.unidade) return setErro("Unidade não informada!");
    if (!local) return setErro("Local de estoque não selecionado não informada!");
    onConfirmar({
      unidade: f.unidade, peso: v(f.peso), qtdpec: v(f.qtdpec), metros: v(f.metros), compmin: v(f.compmin), compmax: v(f.compmax),
      codloc: local.codloc, senhaEncarregado: local.senha, sucata: v(f.sucata), pontas: v(f.pontas),
      compminPontas: v(f.compminP), compmaxPontas: v(f.compmaxP),
    });
  }

  const campo = (rot: string, k: keyof typeof f, casas: number, sm = true) => (
    <label className={"pcf-campo" + (sm ? " pcf-sm" : "")}>
      <span>{rot}</span>
      <input className="num" inputMode={casas ? "decimal" : "numeric"} value={f[k]} onChange={set(k, casas)} />
    </label>
  );
  return (
    <Modal titulo="Lançamento de Produto" sub={`PCF nº ${numpcf} · Etapa: ${etapa} · Posição nº ${posicao}${nompro ? " · " + nompro : ""}`} largura={980} onClose={onVoltar}>
      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />
      <div ref={raiz} onKeyDown={(e) => enterTab(e, raiz.current)}>
        <section className="pcf-sec">
          <h3 style={{ margin: "0 0 8px", fontSize: 14 }}>PRODUTO FINAL</h3>
          <div className="pcf-grid">
            <label className="pcf-campo pcf-sm">
              <span>Unidade</span>
              <select autoFocus value={f.unidade} onChange={(e) => setF({ ...f, unidade: e.target.value })}>
                <option value=""></option>
                <option>KG</option>
                <option>PÇ</option>
              </select>
            </label>
            {campo("Peso", "peso", 2)}
            {campo("Comp. Mínimo", "compmin", 2)}
            {campo("Comp. Máximo", "compmax", 2)}
            {campo("Peças", "qtdpec", 0)}
            {campo("Metros", "metros", 2)}
            <label className="pcf-campo"><span>Local do Estoque</span><input value={local ? `${local.codloc} · ${local.nomloc}` : ""} readOnly tabIndex={-1} /></label>
            <button className="btn-secundario" onClick={() => setAbrirLocal(true)}>Local de Estoque</button>
          </div>
        </section>
        <section className="pcf-sec">
          <h3 style={{ margin: "0 0 8px", fontSize: 14 }}>SUCATA</h3>
          <div className="pcf-grid">{campo("Sucata (Kg)", "sucata", 2)}</div>
        </section>
        <section className="pcf-sec">
          <h3 style={{ margin: "0 0 8px", fontSize: 14 }}>PONTAS</h3>
          <div className="pcf-grid">
            {campo("Comp. Mínimo", "compminP", 2)}
            {campo("Comp. Máximo", "compmaxP", 2)}
            {campo("Peso", "pontas", 2)}
            <label className="pcf-campo"><span>Local do Estoque</span><input value={local ? local.nomloc : ""} readOnly tabIndex={-1} /></label>
          </div>
        </section>
        <div className="pcf-acoes">
          <button className="btn-acao verde" onClick={confirmar}>Confirmar</button>
          <span className="spacer" />
          <button className="btn-secundario" onClick={onVoltar}>Voltar</button>
        </div>
      </div>
      {abrirLocal && (
        <LocalEstoqueModal
          onConfirmar={(l) => {
            setLocal(l);
            setAbrirLocal(false);
          }}
          onVoltar={() => setAbrirLocal(false)}
        />
      )}
    </Modal>
  );
}

// local_estoque (le:120-283): local (lupa = locais ATIVOS) + senha do encarregado; "<nome> deseja confirmar o local de estoque?"
function LocalEstoqueModal({ onConfirmar, onVoltar }: { onConfirmar: (l: { codloc: number; nomloc: string; senha: string }) => void; onVoltar: () => void }) {
  const [codloc, setCodloc] = useState("");
  const [senha, setSenha] = useState("");
  const [locais, setLocais] = useState<{ codloc: number; nomloc: string }[]>([]);
  const [erro, setErro] = useState<string | null>(null);
  const [pergunta, setPergunta] = useState<{ texto: string; nomloc: string } | null>(null);
  const raiz = useRef<HTMLDivElement>(null);
  useEffect(() => {
    apiApont.lancamentoDados(0).then((d) => setLocais(d.locais ?? [])).catch(() => setLocais([]));
  }, []);
  async function confirmar() {
    try {
      const r = await apiApont.localEstoque(Number(codloc) || 0, senha);
      if (!r.ok) return setErro(r.erro ?? "Falha na validação.");
      setPergunta({ texto: `${(r.nomusu ?? "").split(" ")[0]} deseja confirmar o local de estoque?`, nomloc: r.nomloc ?? "" });
    } catch (e) {
      setErro((e as Error).message);
    }
  }
  const nome = locais.find((l) => l.codloc === Number(codloc))?.nomloc ?? "";
  return (
    <Modal titulo="Local de Estoque" sub="Confirmação do encarregado" largura={520} onClose={onVoltar}>
      <MsgModal texto={erro} tipo="erro" onClose={() => setErro(null)} />
      <Confirma texto={pergunta?.texto ?? null} onSim={() => pergunta && onConfirmar({ codloc: Number(codloc), nomloc: pergunta.nomloc, senha })} onNao={() => setPergunta(null)} />
      <div ref={raiz} onKeyDown={(e) => enterTab(e, raiz.current)}>
        <div className="pcf-grid">
          <label className="pcf-campo">
            <span>Local de Estoque</span>
            <select autoFocus value={codloc} onChange={(e) => setCodloc(e.target.value)}>
              <option value=""></option>
              {locais.map((l) => (
                <option key={l.codloc} value={l.codloc}>{l.codloc} · {l.nomloc}</option>
              ))}
            </select>
          </label>
          <label className="pcf-campo">
            <span>Senha Encarregado</span>
            <input type="password" value={senha} autoComplete="off" onChange={(e) => setSenha(e.target.value)} />
          </label>
        </div>
        {nome && <p className="pcf-hint">{nome}</p>}
        <div className="pcf-acoes">
          <button className="btn-acao verde" onClick={() => void confirmar()}>Confirmar</button>
          <span className="spacer" />
          <button className="btn-secundario" onClick={onVoltar}>Voltar</button>
        </div>
      </div>
    </Modal>
  );
}
